import React, { useState, useRef, useEffect } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { Menu, X, Moon, Sun, ChevronRight, Home, User, LogOut, ChevronDown, FileText, BookOpen } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../contexts/AuthContext';

interface NavigationItem {
  title: string;
  path?: string;
  icon?: typeof BookOpen;
  items?: Array<{
    title: string;
    path: string;
  }>;
}

// Navigation structure
const navigationItems: NavigationItem[] = [
  {
    title: 'Digital Tools',
    items: [
      { title: 'DeepBoard - Kanban + Pomodoro', path: '/deepboard' },
      { title: 'Gmail Invoice Extraction Tool', path: '/invoice_processor' },
      { title: 'High Accuracy Invoice OCR', path: '/invoice-ocr' },
      { title: 'Generate Invoice', path: '/invoice-generator' },
      { title: 'Bank Statement to Excel', path: '/bank-statement-converter' },
      { title: 'Duplicate Removal Tool', path: '/duplicate-removal-tool' },
      { title: 'Universal Data Cleaner', path: 'https://data-cleaning.com' },
    ]
  },
  {
    title: 'Data Cleaning Services',
    items: [
      { title: 'Professional Data Cleansing', path: '/data-cleansing-services' },
      { title: '10 Signs You Need Data Cleaning', path: '/when-you-need-professional-data-cleaning' },
    ]
  },
  {
    title: 'Excel & Spreadsheet Guides',
    items: [
      { title: 'Excel Data Cleaning Guide', path: '/excel-data-cleaning-guide' },
      { title: 'Excel Remove Duplicates Guide', path: '/excel-remove-duplicates-guide' },
      { title: 'Excel Text Functions Guide', path: '/excel-text-functions-guide' },
      { title: 'Google Sheets Duplicates Guide', path: '/google-sheets-remove-duplicates-guide' },
      { title: 'Power Query Duplicates Guide', path: '/power-query-remove-duplicates-guide' },
    ]
  },
  {
    title: 'Data Quality & CRM Guides',
    items: [
      { title: 'Data Cleanup Checklist', path: '/data-cleanup-checklist' },
      { title: 'CRM Data Hygiene Guide', path: '/crm-data-hygiene-guide' },
      { title: 'Address Cleansing Guide', path: '/address-cleansing-guide' },
    ]
  },
  {
    title: 'Documentation & Help',
    items: [
      { title: 'Dutch Invoice Requirements', path: '/invoice_processor/invoice-rules-nl' },
      { title: 'CAMT.053 Bank Statement Guide', path: '/invoice_processor/camt053-guide' },
      { title: 'MT940 Bank Format Guide', path: '/invoice_processor/mt940-guide' },
      { title: 'E-Invoicing Implementation Guide', path: '/invoice_processor/e-invoicing-guide' },
    ]
  }
];

const Layout: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const { user, logout } = useAuth();

  // Handle click outside of profile menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setProfileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Close profile menu when location changes
  useEffect(() => {
    setProfileMenuOpen(false);
  }, [location]);

  // Get current page name for breadcrumb
  const getCurrentPageName = () => {
    const path = location.pathname.split('/').pop();
    switch(path) {
      case 'privacy':
        return 'Privacy Policy';
      case 'tos':
        return 'Terms of Service';
      case 'invoice-rules-nl':
        return 'Invoice Rules';
      case 'invoice-magnet':
        return 'Invoice Magnet';
      case 'invoice-generator':
        return 'Invoice Generator';
      case 'bank-statement-converter':
        return 'Bank Statement Converter';
      case 'duplicate-removal-tool':
        return 'Duplicate Removal Tool';
      case 'data-cleansing-services':
        return 'Data Cleansing Services';
      case 'camt053-guide':
        return 'CAMT.053 Guide';
      case 'mt940-guide':
        return 'MT940 Guide';
      case 'e-invoicing-guide':
        return 'E-Invoicing Guide';
      case 'excel-remove-duplicates-guide':
        return 'Excel Duplicates Guide';
      case 'excel-data-cleaning-guide':
        return 'Excel Data Cleaning Guide';
      case 'excel-text-functions-guide':
        return 'Excel Text Functions Guide';
      case 'google-sheets-remove-duplicates-guide':
        return 'Google Sheets Duplicates Guide';
      case 'crm-data-hygiene-guide':
        return 'CRM Data Hygiene Guide';
      case 'address-cleansing-guide':
        return 'Address Cleansing Guide';
      case 'data-cleanup-checklist':
        return 'Data Cleanup Checklist';
      case 'power-query-remove-duplicates-guide':
        return 'Power Query Duplicates Guide';
      case 'when-you-need-professional-data-cleaning':
        return '10 Signs You Need Data Cleaning';
      case 'tools':
        return 'Digital Tools';
      case 'deepboard':
        return 'DeepBoard - Kanban + Pomodoro Timer';
      default:
        return 'Home';
    }
  };

  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''}`}>
      <Helmet>
        <html lang="en" />
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="theme-color" content={darkMode ? '#1f2937' : '#ffffff'} />
        
        {/* Default meta tags that can be overridden by individual pages */}
        <title>Hermes BI - Invoice Tools</title>
        <meta name="description" content="Professional invoice management tools for Dutch businesses" />
        <meta name="keywords" content="gmail invoice extraction tool, invoice generator, Dutch business, BTW, VAT" />
        
        {/* Open Graph defaults */}
        <meta property="og:site_name" content="Hermes BI" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="/logo192_192x192.png" />
        
        {/* Twitter Card defaults */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content="@HermesBI" />
        
        {/* Favicon */}
        <link rel="icon" href="/favicon.ico" />
        <link rel="apple-touch-icon" href="/logo192.png" />
      </Helmet>

      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow-sm fixed w-full z-20 transition-all duration-300 ease-in-out">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-10">
              {/* Logo and Product Name */}
              <Link to="/" className="flex items-center space-x-3">
                <img 
                  src="/logo192_192x192.png" 
                  alt="Hermes BI Logo" 
                  className="h-9 w-9" 
                />
                <span className="text-2xl font-bold text-gray-900 dark:text-white">Hermes BI</span>
              </Link>

              {/* Product Navigation */}
              <nav className="hidden lg:flex items-center">
                {navigationItems.map((item, index) => (
                  <React.Fragment key={item.title}>
                    <div
                      className="relative group"
                      onMouseEnter={() => setOpenDropdown(item.title)}
                      onMouseLeave={() => setOpenDropdown(null)}
                    >
                      <button
                        className="text-gray-700 dark:text-gray-200 hover:text-blue-600 dark:hover:text-blue-400 font-medium py-2 px-4 transition-colors duration-200 text-sm"
                      >
                        {item.title}
                      </button>
                    
                      {/* Dropdown Menu */}
                      <div
                        className={`absolute left-0 mt-1 w-72 rounded-lg shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 transition-all duration-200 ${
                          openDropdown === item.title
                            ? 'opacity-100 visible transform translate-y-0'
                            : 'opacity-0 invisible transform -translate-y-2'
                        }`}
                      >
                        <div className="py-2">
                          {item.items?.map((subItem) => (
                            subItem.path.startsWith('http') ? (
                              <a
                                key={subItem.path}
                                href={subItem.path}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150"
                              >
                                {subItem.title}
                              </a>
                            ) : (
                              <Link
                                key={subItem.path}
                                to={subItem.path}
                                className="block px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150"
                              >
                                {subItem.title}
                              </Link>
                            )
                          ))}
                        </div>
                      </div>
                    </div>
                    
                    {/* Separator */}
                    {index < navigationItems.length - 1 && (
                      <div className="h-5 w-px bg-gray-300 dark:bg-gray-600 mx-1"></div>
                    )}
                  </React.Fragment>
                ))}
              </nav>
            </div>

            <div className="flex items-center space-x-4">
              {/* Theme Toggle */}
              <button
                onClick={() => setDarkMode(!darkMode)}
                className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-200"
                aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
              >
                {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              </button>

              {/* User Profile */}
              {user ? (
                <div className="relative" ref={profileMenuRef}>
                  <button 
                    onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                    className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
                  >
                    {user.picture ? (
                      <img src={user.picture} alt={user.name} className="h-8 w-8 rounded-full" />
                    ) : (
                      <User className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                    )}
                    <span className="text-sm text-gray-700 dark:text-gray-300 hidden sm:block">{user.name}</span>
                  </button>
                  
                  {profileMenuOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg py-2 z-10">
                      <Link
                        to="/profile"
                        className="flex items-center px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150"
                        onClick={() => setProfileMenuOpen(false)}
                      >
                        <User className="h-4 w-4 mr-3" />
                        Profile Settings
                      </Link>
                      <button
                        onClick={() => {
                          setProfileMenuOpen(false);
                          logout();
                        }}
                        className="flex items-center w-full px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150"
                      >
                        <LogOut className="h-4 w-4 mr-3" />
                        Sign Out
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  to="/login"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 transition-colors duration-200"
                >
                  Sign In
                </Link>
              )}

              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileMenuOpen(true)}
                className="lg:hidden text-gray-600 dark:text-gray-300 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
                aria-label="Open navigation menu"
                aria-expanded={mobileMenuOpen}
                aria-controls="mobile-menu"
              >
                <Menu className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Breadcrumb */}
          <div className="mt-3 flex items-center text-sm text-gray-500 dark:text-gray-400">
            <Link to="/" className="hover:text-blue-600 dark:hover:text-blue-400 flex items-center transition-colors duration-200">
              <Home className="h-4 w-4" />
              <span className="ml-2">Hermes BI</span>
            </Link>
            {/* Breadcrumb logic for different top-level routes */}
            {location.pathname === '/tools' ? (
              <>
                <ChevronRight className="h-4 w-4 mx-2" />
                <span className="text-gray-600 dark:text-gray-300">Digital Tools</span>
              </>
            ) : location.pathname === '/invoice-generator' ? (
              <>
                <ChevronRight className="h-4 w-4 mx-2" />
                <Link to="/tools" className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200">
                  Digital Tools
                </Link>
                <ChevronRight className="h-4 w-4 mx-2" />
                <span className="text-gray-600 dark:text-gray-300">Invoice Generator</span>
              </>
            ) : location.pathname.startsWith('/invoice-ocr') ? (
              <>
                <ChevronRight className="h-4 w-4 mx-2" />
                <Link to="/tools" className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200">
                  Digital Tools
                </Link>
                <ChevronRight className="h-4 w-4 mx-2" />
                <span className="text-gray-600 dark:text-gray-300">High Accuracy Invoice OCR</span>
              </>
            ) : location.pathname.startsWith('/duplicate-removal-tool') ? (
              <>
                <ChevronRight className="h-4 w-4 mx-2" />
                <Link to="/tools" className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200">
                  Digital Tools
                </Link>
                <ChevronRight className="h-4 w-4 mx-2" />
                <span className="text-gray-600 dark:text-gray-300">Duplicate Removal Tool</span>
              </>
            ) : location.pathname.startsWith('/data-cleansing-services') || location.pathname.startsWith('/when-you-need-professional-data-cleaning') ? (
              <>
                <ChevronRight className="h-4 w-4 mx-2" />
                <span className="text-gray-600 dark:text-gray-300">Data Cleaning Services</span>
                {location.pathname !== '/data-cleansing-services' && (
                  <>
                    <ChevronRight className="h-4 w-4 mx-2" />
                    <span className="text-gray-600 dark:text-gray-300">{getCurrentPageName()}</span>
                  </>
                )}
              </>
            ) : location.pathname.startsWith('/excel-remove-duplicates-guide') || location.pathname.startsWith('/excel-data-cleaning-guide') || location.pathname.startsWith('/excel-text-functions-guide') || location.pathname.startsWith('/google-sheets-remove-duplicates-guide') || location.pathname.startsWith('/power-query-remove-duplicates-guide') ? (
              <>
                <ChevronRight className="h-4 w-4 mx-2" />
                <span className="text-gray-600 dark:text-gray-300">Excel & Spreadsheet Guides</span>
                <ChevronRight className="h-4 w-4 mx-2" />
                <span className="text-gray-600 dark:text-gray-300">{getCurrentPageName()}</span>
              </>
            ) : location.pathname.startsWith('/data-cleanup-checklist') || location.pathname.startsWith('/crm-data-hygiene-guide') || location.pathname.startsWith('/address-cleansing-guide') ? (
              <>
                <ChevronRight className="h-4 w-4 mx-2" />
                <span className="text-gray-600 dark:text-gray-300">Data Quality & CRM Guides</span>
                <ChevronRight className="h-4 w-4 mx-2" />
                <span className="text-gray-600 dark:text-gray-300">{getCurrentPageName()}</span>
              </>
            ) : (
              <>
                <ChevronRight className="h-4 w-4 mx-2" />
                <Link to="/tools" className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200">
                  Digital Tools
                </Link>
                <ChevronRight className="h-4 w-4 mx-2" />
                <Link to="/invoice-ocr" className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200">
                  High Accuracy Invoice OCR
                </Link>
                {location.pathname !== '/invoice_processor' && (
                  <>
                    <ChevronRight className="h-4 w-4 mx-2" />
                    <span className="text-gray-600 dark:text-gray-300">{getCurrentPageName()}</span>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <div 
        className={`fixed inset-0 z-50 transform transition-transform duration-300 ${
          mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="absolute inset-0 bg-black opacity-50" onClick={() => setMobileMenuOpen(false)}></div>
        <div className="absolute right-0 h-full w-72 bg-white dark:bg-gray-800 shadow-xl overflow-y-auto">
          <div className="p-6">
            <button 
              onClick={() => setMobileMenuOpen(false)}
              className="absolute top-4 right-4 text-gray-600 dark:text-gray-300 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
              aria-label="Close navigation menu"
            >
              <X className="h-6 w-6" />
            </button>
            <nav className="mt-12">
              {/* All Sections */}
              {navigationItems.map(category => (
                  <div key={category.title} className="mb-8">
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4">
                      {category.title}
                    </h3>
                    <ul className="space-y-1">
                      {category.items?.map(item => (
                        <li key={item.path}>
                          {item.path.startsWith('http') ? (
                            <a
                              href={item.path}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block py-3 px-4 text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors duration-150"
                              onClick={() => setMobileMenuOpen(false)}
                            >
                              {item.title}
                            </a>
                          ) : (
                            <Link
                              to={item.path}
                              className="block py-3 px-4 text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors duration-150"
                              onClick={() => setMobileMenuOpen(false)}
                            >
                              {item.title}
                            </Link>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
            </nav>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="pt-[104px]">
        {children ? children : <Outlet />}
      </div>

      {/* Footer */}
      <footer className="bg-gray-800 dark:bg-gray-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <h3 className="text-lg font-semibold mb-4">Hermes BI</h3>
              <p className="text-gray-400">Modern financial tools and services for modern businesses</p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/" className="text-gray-400 hover:text-white transition duration-300">
                    Home
                  </Link>
                </li>
                <li>
                  <Link to="/data-cleansing-services" className="text-gray-400 hover:text-white transition duration-300">
                    Data Cleaning Services
                  </Link>
                </li>
                <li>
                  <Link to="/invoice-ocr" className="text-gray-400 hover:text-white transition duration-300">
                    Invoice OCR Tool
                  </Link>
                </li>
                <li>
                  <Link to="/duplicate-removal-tool" className="text-gray-400 hover:text-white transition duration-300">
                    Duplicate Removal Tool
                  </Link>
                </li>
                <li>
                  <Link to="/invoice_processor/privacy" className="text-gray-400 hover:text-white transition duration-300">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/invoice_processor/tos" className="text-gray-400 hover:text-white transition duration-300">
                    Terms of Service
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Contact</h3>
              <p className="text-gray-400">
                Email: support@hermes-bi.com<br />
                Location: Amsterdam, Netherlands
              </p>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-gray-700 text-center text-gray-400">
            <p>&copy; {new Date().getFullYear()} Hermes Business Intelligence. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout; 